import * as THREE from 'three';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { CARS } from '../catalog.js';
import { circuitFor } from '../circuits.js';
import { planBroadcastShot } from './broadcast.js';
export { planBroadcastShot } from './broadcast.js';
import { animateCar } from './cars.js';
import { loadAuthoredCars } from './authored-cars.js';
import { buildWorld } from './world.js';

const clamp = THREE.MathUtils.clamp;
const smooth = value => value * value * (3 - 2 * value);

// Showcase location on the circuit: the car parks on the barrier-lined straight
// so the ZalaFun GT board sits in the lower-left of the hero shot. The framed
// distance keeps that board about 24 m from the camera, which keeps the whole
// panel inside the frame instead of running off the edge.
const heroSpots = {
  straight: { distance: 150, lane: -5.4, turn: -.16, camera: [3.7, 7.1, 1.0], look: [0, .3, .8] },
  grid: { distance: 26, lane: 3.4, turn: -.14, camera: [3.6, 6.9, .98], look: [0, .3, .78] },
  corner: { distance: 548, lane: -3.2, turn: -.18, camera: [3.9, 7.3, 1.02], look: [0, .3, .8] },
};
const heroSpotId = new URLSearchParams(location.search).get('spot');
const heroSpot = heroSpots[heroSpotId] || heroSpots.straight;

function contactShadow() {
  const canvas = document.createElement('canvas');
  canvas.width = 128; canvas.height = 256;
  const context = canvas.getContext('2d');
  const gradient = context.createRadialGradient(64, 128, 8, 64, 128, 120);
  gradient.addColorStop(0, '#071018a8'); gradient.addColorStop(.5, '#0710185c'); gradient.addColorStop(1, '#07101800');
  context.fillStyle = gradient; context.fillRect(0, 0, 128, 256);
  const material = new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(canvas), transparent: true, depthWrite: false, opacity: .64 });
  const shadow = new THREE.Mesh(new THREE.PlaneGeometry(2.6, 5.5), material);
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = .024;
  shadow.renderOrder = 1;
  return shadow;
}

export class RaceScene {
  constructor(container, onContext, onProgress) {
    this.onProgress = onProgress;
    this.container = container;
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance', preserveDrawingBuffer: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = .93;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.prepend(this.renderer.domElement);
    this.renderer.domElement.setAttribute('aria-hidden', 'true');
    this.renderer.domElement.addEventListener('webglcontextlost', event => { event.preventDefault(); onContext(false); });
    this.renderer.domElement.addEventListener('webglcontextrestored', () => onContext(true));
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog('#c8d8e4', 230, 850);
    this.ambient = new THREE.HemisphereLight('#d9e8fc', '#626758', 1.0);
    this.scene.add(this.ambient);
    this.sun = new THREE.DirectionalLight('#fff0dc', 3.0);
    this.sun.position.set(45, 100, 60);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    Object.assign(this.sun.shadow.camera, { left: -90, right: 90, top: 90, bottom: -90, near: 1, far: 280 });
    this.sun.shadow.bias = -.00012; this.sun.shadow.normalBias = .012;
    this.scene.add(this.sun, this.sun.target);
    this.circuit = circuitFor(null);
    this.world = buildWorld(this.circuit);
    this.scene.add(this.world.root);
    this.cars = new Map();
    this.camera = new THREE.PerspectiveCamera(42, 1, .12, 8000);
    this.position = new THREE.Vector3();
    this.look = new THREE.Vector3();
    this.targetPosition = new THREE.Vector3();
    this.targetLook = new THREE.Vector3();
    this.cameraReady = false;
    this.lastMode = '';
    this.previousTime = 0;
    this.selectedId = 'ember';
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
    this.resize();
    this.ready = this.prepareAssets();
  }

  async prepareAssets() {
    const report = (id, patch) => this.onProgress?.({ id, ...patch });
    const [cars] = await Promise.all([loadAuthoredCars(report), this.prepareLighting()]);
    this.cars = cars;
    const shadow = contactShadow();
    for (const group of cars.values()) {
      group.add(shadow.clone());
      this.scene.add(group);
    }
    this.thumbnails = await this.makeThumbnails();
    this.resize();
  }

  async prepareLighting() {
    // An HDR environment supplies the range and detail that lacquer and glass reflect.
    const daylight = await new RGBELoader().loadAsync('assets/circuit-daylight-v4.hdr',
      event => this.onProgress?.({ id: 'daylight', loaded: event.loaded, total: event.total }));
    daylight.mapping = THREE.EquirectangularReflectionMapping;
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const environment = pmrem.fromEquirectangular(daylight);
    this.environment = environment;
    this.daylight = daylight;
    this.scene.environment = environment.texture;
    this.scene.environmentIntensity = .72;
    this.scene.background = daylight;
    this.scene.backgroundIntensity = .74;
    this.scene.backgroundRotation.y = -.55;
    this.scene.environmentRotation.y = -.55;
    this.sun.intensity = 2.1;
    pmrem.dispose();
    this.onProgress?.({ id: 'daylight', decoded: true });
  }

  resize() {
    const width = this.container.clientWidth, height = this.container.clientHeight;
    if (!width || !height) return;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  async makeThumbnails() {
    const thumbnails = {};
    const scene = new THREE.Scene();
    scene.environment = this.environment.texture;
    scene.environmentIntensity = .72;
    scene.environmentRotation.y = -.55;
    scene.add(new THREE.HemisphereLight('#d9e8fc', '#626758', 1.0));
    const light = new THREE.DirectionalLight('#fff0dc', 2.1);
    light.position.set(3, 6, 7); scene.add(light);
    const camera = new THREE.PerspectiveCamera(18, 2, .1, 50);
    camera.position.set(4.1, 1.9, 7.0); camera.lookAt(0, .55, 0);
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(280, 140, false);
    this.renderer.setClearColor(0xffffff, 0);
    this.renderer.shadowMap.enabled = false;
    for (const car of CARS) {
      const model = this.cars.get(car.id);
      scene.add(model); model.position.set(0, 0, 0); model.rotation.set(0, 0, 0);
      this.renderer.render(scene, camera);
      thumbnails[car.id] = this.renderer.domElement.toDataURL('image/png');
      this.scene.add(model);
      // Five renders in a row would hold the main thread and freeze the loading
      // ring, so hand every frame back to the browser between cars.
      await new Promise(resolve => requestAnimationFrame(resolve));
    }
    this.renderer.shadowMap.enabled = true;
    this.renderer.setClearColor('#8ebde0', 1);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    return thumbnails;
  }

  setView(position, target, fov, snap = false) {
    this.targetPosition.set(position.x, position.y, position.z);
    this.targetLook.set(target.x, target.y, target.z);
    this.targetFov = fov;
    if (snap || !this.cameraReady) {
      this.position.copy(this.targetPosition); this.look.copy(this.targetLook);
      this.camera.fov = fov; this.cameraReady = true;
    }
  }

  offset(distance, lane, right, forward, height) {
    const pose = this.circuit.pose(distance + forward, lane + right);
    return { x: pose.x, y: height, z: pose.z };
  }

  frameContenders(position, target, fov, cars) {
    const aim = new THREE.Vector3(target.x, target.y, target.z);
    const back = new THREE.Vector3(position.x, position.y, position.z).sub(aim);
    let distance = back.length(); back.normalize();
    const right = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), back).normalize();
    const up = new THREE.Vector3().crossVectors(back, right);
    const vertical = Math.tan(THREE.MathUtils.degToRad(fov / 2));
    const horizontal = vertical * this.camera.aspect;
    for (const car of cars) {
      const pose = car.pose || this.circuit.pose(car.distance, car.lane);
      for (const side of [-1.3, 1.3]) for (const forward of [-2.7, 2.7]) for (const height of [.03, 1.65]) {
        const point = new THREE.Vector3(pose.x + side * Math.cos(pose.heading) + forward * Math.sin(pose.heading), height,
          pose.z - side * Math.sin(pose.heading) + forward * Math.cos(pose.heading)).sub(aim);
        distance = Math.max(distance, point.dot(back) + Math.abs(point.dot(right)) / (horizontal * .88),
          point.dot(back) + Math.abs(point.dot(up)) / (vertical * .78));
      }
    }
    return aim.addScaledVector(back, distance);
  }

  showCar(id) { this.selectedId = id || 'ember'; }

  render({ stage, elapsed = 0, sample = null, selectedCarId = null, winnerId = null, time = 0, round = null }) {
    const circuit = circuitFor(round);
    if (this.circuit.id !== circuit.id) {
      this.scene.remove(this.world.root); this.world.dispose();
      this.circuit = circuit; this.world = buildWorld(circuit); this.scene.add(this.world.root);
      this.camera.far = circuit.id === 'flow-s' ? 8000 : 1200;
      this.cameraReady = false;
    }
    const delta = Math.min(.1, Math.max(0, time - this.previousTime));
    this.previousTime = time;
    let focus = { x: -18, y: 0, z: -26 };
    let mode = stage;
    if (stage === 'selecting' || stage === 'loading') {
      const pose = this.circuit.pose(heroSpot.distance, heroSpot.lane);
      const yaw = heroSpot.turn + pose.heading;
      for (const [id, model] of this.cars) {
        model.visible = id === this.selectedId;
        if (!model.visible) continue;
        model.position.set(pose.x, .02, pose.z);
        model.rotation.set(0, yaw, 0);
        animateCar(model, { speed: 0, time });
      }
      const heroFov = THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(20)) / this.camera.aspect));
      focus = { x: pose.x, y: 0, z: pose.z };
      const at = (side, forward, height) => ({
        x: pose.x + side * Math.cos(yaw) + forward * Math.sin(yaw),
        y: height,
        z: pose.z - side * Math.sin(yaw) + forward * Math.cos(yaw),
      });
      this.setView(at(...heroSpot.camera), at(...heroSpot.look), heroFov, this.lastMode === 'result');
    } else if (sample) {
      for (const car of sample.cars) {
        const model = this.cars.get(car.id);
        const pose = car.pose || this.circuit.pose(car.distance, car.lane);
        model.visible = true;
        model.position.set(pose.x, .020, pose.z);
        model.rotation.set(0, pose.heading, 0);
        const inBend = pose.heading > 0 && pose.heading < Math.PI;
        animateCar(model, { speed: car.speed, steering: car.steering ?? (inBend ? .07 : 0), braking: stage === 'racing' && car.acceleration < -2, time });
      }
      const leading = sample.cars.find(car => car.id === sample.leader);
      const runnerUp = sample.cars.find(car => car.id === sample.order[1]);
      const chosen = sample.cars.find(car => car.id === selectedCarId) || leading;
      focus = leading.pose || this.circuit.pose(leading.distance, leading.lane);
      if (stage === 'grid' || stage === 'countdown') {
        const blend = smooth(clamp(elapsed / 2, 0, 1));
        const low = { x: 0, y: 3.0, z: -26.4 };
        const high = { x: 0, y: 12, z: -32 };
        const position = { x: THREE.MathUtils.lerp(high.x, low.x, blend), y: THREE.MathUtils.lerp(high.y, low.y, blend), z: THREE.MathUtils.lerp(high.z, low.z, blend) };
        this.setView(position, { x: 0, y: .5, z: 14 }, 70);
      } else if (stage === 'celebrating' || stage === 'result') {
        const winner = sample.cars.find(car => car.id === winnerId) || leading;
        const drift = this.reducedMotion ? 0 : Math.sin(time * .4) * .3;
        const framing = Math.max(1, .82 / this.camera.aspect);
        this.setView(this.offset(winner.distance, winner.lane, (-5.7 + drift) * framing, 7.8 * framing, 2.1 * framing), this.offset(winner.distance, winner.lane, 0, -.3, stage === 'result' ? -1.5 : .55), 41);
      } else if (round?.raceVersion >= 5) {
        const shot = planBroadcastShot(round, sample, this.camera.aspect);
        mode = shot.mode;
        // These paths are already smooth: track the current subjects so a narrow
        // lens cannot lag behind the cars, or a mounted view slip inside its body.
        const direct = shot.mode === 'hood' || (round.raceVersion >= 6 && (shot.fixed || shot.mode === 'corner-exit'));
        this.setView(shot.position, shot.target, shot.fov, direct || this.lastShotId !== shot.id);
        this.lastShotId = shot.id;
      } else {
        const raceTime = sample.time ?? elapsed - 5;
        const duration = sample.duration ?? 30;
        const subjects = (sample.focusIds || sample.order.slice(0, 2)).map(id => sample.cars.find(car => car.id === id));
        const battleFront = subjects.reduce((first, car) => car.distance > first.distance ? car : first);
        const battleLane = (subjects[0].lane + subjects[1].lane) / 2;
        const battleGap = Math.abs(subjects[0].lane - subjects[1].lane);
        const averageLane = (leading.lane + runnerUp.lane) / 2;
        const gap = Math.abs(leading.lane - runnerUp.lane);
        const speedBlend = clamp((chosen.speed - 22) / 38, 0, 1);
        const vibration = this.reducedMotion ? 0 : Math.sin(time * 41) * .013 * speedBlend;
        if (raceTime > duration - 1.7) {
          mode = 'photo';
          const contenders = sample.order.slice(0, 3).map(id => sample.cars.find(car => car.id === id))
            .filter((car, index) => index < 2 || leading.distance - car.distance < 3);
          const target = this.offset(leading.distance, averageLane, 0, .1, .65);
          const position = this.offset(leading.distance, averageLane, -2.4, 17.5, 2.4);
          this.setView(this.frameContenders(position, target, 53, contenders), target, 53);
        } else if (sample.braking) {
          mode = 'braking';
          this.setView(this.offset(leading.distance, leading.lane, -.85, -9.2, 1.85), this.offset(leading.distance, leading.lane, 0, 2, .38), 65);
        } else if (sample.phase === 'corner') {
          mode = 'corner';
          const portrait = Math.max(1, .62 / this.camera.aspect);
          this.setView(this.offset(battleFront.distance, battleLane, (-3.4 - battleGap * .18) * portrait, (13.5 + battleGap * .65) * portrait, 2.6), this.offset(battleFront.distance, battleLane, 0, -1.6, .38), 57);
        } else if (sample.event || (!sample.duration && ((raceTime > 5 && raceTime < 6.6) || (raceTime > 24 && raceTime < 26.8)))) {
          mode = 'duel';
          this.setView(this.offset(battleFront.distance, battleLane, -10.8 - battleGap * .25, 5.8, 2.2), this.offset(battleFront.distance, battleLane, 0, -1.8, .45), 58);
        } else {
          mode = 'chase';
          this.setView(this.offset(chosen.distance, chosen.lane, .65, -8.7, 1.85 + vibration), this.offset(chosen.distance, chosen.lane, 0, 1.2, .38), 61 + speedBlend * 7);
        }
      }
    }
    const lamps = stage === 'countdown' ? Math.min(5, Math.floor((elapsed - 2) / .47) + 1) : 0;
    this.world.startLamps.forEach((lamp, index) => { lamp.material.color.set(index < lamps ? '#ff2635' : '#321015'); });
    this.world.update(time, stage === 'celebrating' || stage === 'result' ? 1 : 0, this.position);
    const hero = stage === 'selecting' || stage === 'loading';
    this.ambient.intensity = hero ? .16 : 1.0;
    this.ambient.color.set(hero ? '#c6dcff' : '#d9e8fc');
    this.ambient.groundColor.set(hero ? '#4c5148' : '#626758');
    this.scene.environmentIntensity = hero ? .24 : .72;
    this.scene.backgroundIntensity = hero ? .5 : .74;
    this.sun.intensity = hero ? 4.8 : 2.1;
    this.sun.color.set(hero ? '#ffe6b8' : '#fff0dc');
    this.renderer.toneMappingExposure = hero ? 1.1 : .93;
    this.sun.target.position.set(focus.x, 0, focus.z);
    this.sun.position.set(focus.x + (hero ? 34 : 45), hero ? 11 : 100, focus.z + (hero ? 2 : 60));
    const shadowExtent = stage === 'selecting' || stage === 'loading' ? 9 : stage === 'grid' || stage === 'countdown' ? 30 : 37;
    Object.assign(this.sun.shadow.camera, { left: -shadowExtent, right: shadowExtent, top: shadowExtent, bottom: -shadowExtent });
    this.sun.shadow.camera.updateProjectionMatrix();
    // Broadcast cuts avoid travelling through cars or trackside structures.
    if (stage === 'racing' && this.lastMode !== mode) {
      this.position.copy(this.targetPosition); this.look.copy(this.targetLook);
      this.camera.fov = this.targetFov;
    }
    const follow = 1 - Math.exp(-delta * (this.lastMode !== mode ? 7 : 12));
    this.position.lerp(this.targetPosition, follow);
    this.look.lerp(this.targetLook, follow);
    this.camera.position.copy(this.position);
    this.camera.lookAt(this.look);
    this.camera.fov = THREE.MathUtils.lerp(this.camera.fov, this.targetFov, follow);
    this.camera.updateProjectionMatrix();
    this.renderer.render(this.scene, this.camera);
    this.lastMode = mode;
    return { track: this.circuit.id, calls: this.renderer.info.render.calls, triangles: this.renderer.info.render.triangles, camera: mode, progress: sample ? Math.max(0, Math.min(1, Math.max(...sample.cars.map(car => car.frontDistance ?? car.distance)) / this.circuit.length)) : 0 };
  }
}
